import {
  formatLocalElectionOfficeLink,
  formatMailDeadline,
  getRegistrationForm,
  stripReceivedPostmarkedFromDeadline,
  getPollingPlaceLocatorLink,
  electionsByMailOnly,
  getAbsenteeMailLink,
  isOnlineAbsenteeRequestAvailable,
} from '~utils/faqAnswerUtils';
import {
  hasRegionalBallotRequestUrls,
  universalVbmNoTemporary,
  printMailDisabled,
  onlinePortalMustPrintApplication,
} from '~components/Absentee/navigateToSecondStep';
import { capitalize } from '~utils/helpers';

const stubOnlineInstructions = (stateInfo, stateName, stateCode) => {
  if (hasRegionalBallotRequestUrls.includes(stateCode)) {
    return `Some cities or counties in ${stateName} allow you to request your ballot online. 
      [Request your ballot with our tool](/absentee-mail-ballot/) to determine if yours does. 
      If an online request portal is not available for your area, our tool will email you 
      a ballot request form that you can print, sign, and mail.`;
  }

  if (stateInfo.absentee_request_methods.multiselect_value.includes('online')) {
    const paragraphs = [];
    const steps = [];

    steps.push(
      `1. Visit the [${stateName} online ballot request site](${stateInfo.gov_tool_absentee_request.text}).`
    );
    steps.push(`2. Fill out all of the required fields.`);
    steps.push(
      `3. You should receive your ballot in the mail ahead of the election.`
    );
    paragraphs.push(steps.join('\n'));

    if (stateInfo.absentee_directions_online.text) {
      paragraphs.push(
        `Here&apos;s some extra information to guide you through the ${stateName} application:`
      );
      paragraphs.push(stateInfo.absentee_directions_online.text);
    }

    paragraphs.push(
      `Requests must be submitted by ${stateInfo.absentee_deadline_online.text}.`
    );
    return paragraphs.join('\n\n');
  }
  return `${stateName} doesn't accept ballot requests online.`;
};

const stubMailInstructions = (stateInfo, stateName, stateCode) => {
  if (stateInfo.absentee_request_methods.multiselect_value.includes('mail')) {
    if (stateInfo.absentee_directions_mail.text) {
      return stateInfo.absentee_directions_mail.text;
    }

    const steps = [];

    if (onlinePortalMustPrintApplication.includes(stateCode)) {
      steps.push(
        `1. Visit the [${stateName} absentee ballot request portal](${getAbsenteeMailLink(stateInfo, stateCode)}).
        It will help you to generate a form to print and mail.`
      );
    } else {
      steps.push(
        `1. Download the [${stateName} absentee ballot application form](${getAbsenteeMailLink(stateInfo, stateCode)}).`
      );
    }
    steps.push(`2. Fill out the required fields.`);
    steps.push(`3. Print the application.`);
    steps.push(
      `4. **Sign and date your application. This is very important**.`
    );
    steps.push(
      `5. Mail or hand-deliver your completed application directly to your 
      ${formatLocalElectionOfficeLink(stateCode)} as soon as possible, but no later than the deadline. 
      (It must be ${formatMailDeadline(stateInfo.absentee_deadline_mail.text)}.)`
    );

    return steps.join('\n');
  }
  return `${stateName} doesn't accept ballot requests via mail.`;
};

const stubInPersonInstructions = (stateInfo, stateName, stateCode) => {
  if (stateInfo.absentee_request_methods.multiselect_value.includes('mail')) {
    return `Contact your ${formatLocalElectionOfficeLink(stateCode)} to find out where to request a ballot in person. 
    The deadline to apply is ${stateInfo.absentee_deadline_in_person.text}.`;
  }
  return `${stateName} doesn't accept ballot requests in person.`;
};

const stubUniversalVbmExplanation = (stateInfo, stateName, stateCode) => {
  if (
    stateInfo.absentee_access_levels.text === 'universal' &&
    !universalVbmNoTemporary.includes(stateCode)
  ) {
    const paragraphs = [];
    paragraphs.push(
      `${stateName} will automatically send a mail-in ballot to every registered voter ahead of all elections.`
    );

    const items = [];
    items.push(
      `* If you need to update your mailing address permanently, 
      [register to vote](/register-to-vote/) again using your new mailing address.`
    );
    items.push(
      `* If you need to update your mailing address temporarily — 
      because you will be out of town for a specific election — 
      [request a one-time absentee ballot](/absentee-mail-ballot/).`
    );
    paragraphs.push(items.join('\n'));
    return paragraphs.join('\n\n');
  }
  return '';
};

const stubNoOneTimeRequestsExplanation = (stateName, stateCode) => {
  if (universalVbmNoTemporary.includes(stateCode)) {
    return `${stateName} does not support one-time absentee ballots. 
      If you will be away from home during a specific election, 
      you will need to [register to vote](/register-to-vote/) again and 
      update the address where you want to receive your ballot.`;
  }
  return '';
};

export const whatAreTheVoteByMailDeadlines = (
  stateInfo,
  stateName,
  stateCode
) => {
  const inPersonBallotReturnAvailable =
    stateInfo.ballot_return_locations.multiselect_value.length > 0 &&
    stateInfo.ballot_return_locations.text !== '[]';

  const paragraphs = [];

  if (universalVbmNoTemporary.includes(stateCode)) {
    paragraphs.push(`${stateName} does not have one-time absentee ballot requests. 
    The state will automatically send a ballot to every registered voter for each election. 
    If you need to receive your ballot at a different location, you should [update your voter registration](/register-to-vote/). 
    Here are the deadlines to do so:`);

    const bullets = [];
    if (stateInfo.registration_methods.multiselect_value.includes('online')) {
      bullets.push(
        `* [Online](${stateInfo.gov_tool_registration.text}): ${stateInfo.registration_deadline_online.text}`
      );
    }
    if (stateInfo.registration_methods.multiselect_value.includes('mail')) {
      bullets.push(
        `* [By mail](${getRegistrationForm(stateInfo, stateCode)}): ${capitalize(formatMailDeadline(stateInfo.registration_deadline_mail.text))}`
      );
    }
    bullets.push(
      `* [In person](${stateInfo.source_registration_info.text}): ${stateInfo.registration_deadline_in_person.text}`
    );

    paragraphs.push(bullets.join('\n'));
  } else {
    paragraphs.push(`Ballot request deadlines in ${stateName} are as follows:`);
    const bullets = [];

    if (isOnlineAbsenteeRequestAvailable(stateInfo, stateCode)) {
      bullets.push(
        `* [Online](${stateInfo.gov_tool_absentee_request.text}): ${stateInfo.absentee_deadline_online.text}`
      );
    }
    if (stateInfo.absentee_request_methods.multiselect_value.includes('mail')) {
      bullets.push(
        `* [By mail](${getAbsenteeMailLink(stateInfo, stateCode)}): ${capitalize(formatMailDeadline(stateInfo.absentee_deadline_mail.text))}`
      );
    }
    bullets.push(
      `* [In person](${stateInfo.source_absentee_info.text}): ${stateInfo.absentee_deadline_in_person.text}`
    );

    paragraphs.push(bullets.join('\n'));
  }

  paragraphs.push(`Ballot return deadlines in ${stateName} are as follows:`);
  const bullets = [];
  bullets.push(
    `* [By mail](/local-election-offices/${stateCode}): ${capitalize(formatMailDeadline(stateInfo.ballot_return_deadline_mail.text))}`
  );
  if (inPersonBallotReturnAvailable) {
    bullets.push(
      `* [In person](/local-election-offices/${stateCode}): ${capitalize(formatMailDeadline(stateInfo.ballot_return_deadline_in_person.text))}`
    );
  }

  paragraphs.push(bullets.join('\n'));

  return paragraphs.join('\n\n');
};

export const canIHaveABallotMailedToMe = (stateInfo, stateName, stateCode) => {
  const paragraphs = [];
  paragraphs.push(stateInfo.absentee_voting_laws.text);

  if (stateInfo.absentee_access_levels.text !== 'universal') {
    paragraphs.push(
      'See below for different ways you can request your ballot:'
    );

    if (
      stateInfo.absentee_request_methods.multiselect_value.includes('online')
    ) {
      paragraphs.push('#### **Online**');
      paragraphs.push(stubOnlineInstructions(stateInfo, stateName));
    }

    if (stateInfo.absentee_request_methods.multiselect_value.includes('mail')) {
      paragraphs.push('#### **By mail**');
      paragraphs.push(stubMailInstructions(stateInfo, stateName, stateCode));
    }

    if (
      stateInfo.absentee_request_methods.multiselect_value.includes('in_person')
    ) {
      paragraphs.push('#### **In person**');
      paragraphs.push(
        stubInPersonInstructions(stateInfo, stateName, stateCode)
      );
    }
  }

  return paragraphs.join('\n\n');
};

export const canIRequestMyBallotOnline = (stateInfo, stateName, stateCode) => {
  if (hasRegionalBallotRequestUrls.includes(stateCode)) {
    return `Some cities or counties in ${stateName} allow you to request your ballot online. 
      [Request your ballot with our tool](/absentee-mail-ballot/) to determine if yours does. 
      If an online request portal is not available for your area, our tool will email you 
      a ballot request form that you can print, sign, and mail.`;
  }

  const mailAlternativeAvailable =
    stateInfo.absentee_request_methods.multiselect_value.includes('mail') &&
    stateInfo.pdf_absentee_form.text &&
    !printMailDisabled.includes(stateCode);
  const alternativeMethods = `Instead, you can ${
    mailAlternativeAvailable
      ? `[print and mail a form](${stateInfo.pdf_absentee_form.text}) or `
      : ''
  } contact your ${formatLocalElectionOfficeLink(stateCode)} to request your ballot.`;
  const paragraphs = [];

  if (stateInfo.absentee_access_levels.text === 'universal') {
    if (universalVbmNoTemporary.includes(stateCode)) {
      return stubNoOneTimeRequestsExplanation(stateName, stateCode);
    }

    paragraphs.push(
      stubUniversalVbmExplanation(stateInfo, stateName, stateCode)
    );

    if (
      stateInfo.absentee_request_methods.multiselect_value.includes('online')
    ) {
      paragraphs.push(
        'If you need a one-time absentee ballot, here are the instructions to request online:'
      );
      paragraphs.push(stubOnlineInstructions(stateInfo, stateName));
    } else {
      paragraphs.push(
        `If you need to request a one-time absentee ballot, you cannot request it online. ${alternativeMethods}`
      );
    }

    return paragraphs.join('\n\n');
  }

  if (isOnlineAbsenteeRequestAvailable(stateInfo, stateCode)) {
    paragraphs.push('Yes. Here are the instructions:');
    paragraphs.push(stubOnlineInstructions(stateInfo, stateName));
  } else {
    paragraphs.push(`No, ${stateName} doesn't accept online ballot requests.`);
    paragraphs.push(alternativeMethods);
  }

  return paragraphs.join('\n\n');
};

export const canIRequestMyBallotViaMail = (stateInfo, stateName, stateCode) => {
  const paragraphs = [];

  if (stateInfo.absentee_access_levels.text === 'universal') {
    if (universalVbmNoTemporary.includes(stateCode)) {
      return stubNoOneTimeRequestsExplanation(stateName, stateCode);
    }

    paragraphs.push(
      stubUniversalVbmExplanation(stateInfo, stateName, stateCode)
    );
    paragraphs.push(
      'If you need a one-time absentee ballot, here are the instructions to request via mail:'
    );
    paragraphs.push(stubMailInstructions(stateInfo, stateName, stateCode));
    return paragraphs.join('\n\n');
  }

  if (stateInfo.absentee_directions_mail.text) {
    return stateInfo.absentee_directions_mail.text;
  }

  paragraphs.push('Yes. Here are the instructions:');
  paragraphs.push(stubMailInstructions(stateInfo, stateName, stateCode));

  return paragraphs.join('\n\n');
};

export const canIRequestMyBallotInPerson = (
  stateInfo,
  stateName,
  stateCode
) => {
  const paragraphs = [];

  if (stateInfo.absentee_access_levels.text === 'universal') {
    if (universalVbmNoTemporary.includes(stateCode)) {
      return stubNoOneTimeRequestsExplanation(stateName, stateCode);
    }

    paragraphs.push(
      stubUniversalVbmExplanation(stateInfo, stateName, stateCode)
    );
    paragraphs.push(stubInPersonInstructions(stateInfo, stateName, stateCode));
    return paragraphs.join('\n\n');
  }

  if (
    stateInfo.absentee_request_methods.multiselect_value.includes('in_person')
  ) {
    paragraphs.push(
      `Yes. ${stubInPersonInstructions(stateInfo, stateName, stateCode)}`
    );
  } else {
    return `No. ${stateName} doesn't accept ballot requests in person.`;
  }

  return paragraphs.join('\n\n');
};

export const doIHaveToRequestABallotEveryTime = (
  stateInfo,
  stateName,
  stateCode
) => {
  if (stateInfo.absentee_access_levels.text === 'universal') {
    return `${stateName} is a universal vote-by-mail state, which means the state will 
    automatically mail a ballot to all registered voters ahead of every election. 
    Once you're registered, there's no further action for you to take to vote by mail.`;
  }

  if (stateInfo.absentee_access_levels.text === 'permanent') {
    const hasOnlineSignup =
      stateInfo.pav_signup_methods.multiselect_value.includes(
        'online_absentee'
      ) ||
      stateInfo.pav_signup_methods.multiselect_value.includes(
        'online_register'
      );
    const hasMailSignup =
      stateInfo.pav_signup_methods.multiselect_value.includes(
        'mail_absentee'
      ) ||
      stateInfo.pav_signup_methods.multiselect_value.includes(
        'mail_register'
      ) ||
      stateInfo.pav_signup_methods.multiselect_value.includes('mail_pav');
    const paragraphs = [];

    paragraphs.push(`No. ${stateName} offers permanent absentee voting. 
    This means you can sign up once to receive ballots in the mail for at least the next 4 years.`);
    paragraphs.push('Here are your options for signing up:');

    if (hasOnlineSignup) {
      paragraphs.push('**Online**');

      if (
        stateInfo.pav_signup_methods.multiselect_value.includes(
          'online_absentee'
        ) &&
        hasRegionalBallotRequestUrls.includes(stateCode)
      ) {
        paragraphs.push(
          `Some cities or counties in ${stateName} allow you to sign up as a permanent absentee voter online.`
        );
        paragraphs.push(
          '[Sign up via our tool](/absentee-mail-ballot/) to see if yours does. ' +
            'If an online request portal is not available for your area, ' +
            'our tool will email you a signup form that you can print, sign, and mail.'
        );
      } else {
        const steps = [];

        if (
          stateInfo.pav_signup_methods.multiselect_value.includes(
            'online_register'
          )
        ) {
          paragraphs.push(
            'You can sign up to be a permanent absentee voter by updating your voter registration online.'
          );
          steps.push(
            `1. Visit the [${stateName} online voter registration site](${stateInfo.gov_tool_registration.text}).`
          );
        } else {
          steps.push(
            `1. Visit the [${stateName} online absentee ballot request site](${stateInfo.gov_tool_absentee_request.text}).`
          );
        }
        steps.push(`2. ${stateInfo.pav_directions_online.text}`);
        steps.push('3. Complete the workflow and submit.');

        paragraphs.push(steps.join('\n'));
      }
    }

    if (hasMailSignup) {
      paragraphs.push('**Via mail**');
      const steps = [];

      if (
        stateInfo.pav_signup_methods.multiselect_value.includes('mail_register')
      ) {
        paragraphs.push(
          'You can sign up to be a permanent absentee voter by updating your voter registration via mail.'
        );
        steps.push(
          `1. Download the [${stateName} voter registration form](${stateInfo.pdf_register_form.text}).`
        );
      } else if (
        stateInfo.pav_signup_methods.multiselect_value.includes('mail_absentee')
      ) {
        steps.push(
          `1. Download the [${stateName} absentee ballot application form](${stateInfo.pdf_absentee_form.text}).`
        );
      } else {
        steps.push(
          `1. Download the [${stateName} permanent absentee voting form](${stateInfo.pdf_pav_form.text}).`
        );
      }

      steps.push(`2. ${stateInfo.pav_directions_mail.text}`);
      steps.push('3. Fill out all other required fields.');
      steps.push('4. Print the application');
      steps.push(
        '5. **Sign and date your application. This is very important**.'
      );
      steps.push(
        `6. Mail or hand-deliver your completed application directly to your ${formatLocalElectionOfficeLink(stateCode)}.`
      );

      paragraphs.push(steps.join('\n'));
    }

    return paragraphs.join('\n\n');
  }

  if (stateInfo.absentee_access_levels.text === 'semi_permanent') {
    return `${stateName} allows you to sign up once to receive ballots for at least 1 calendar year.
    When [requesting your mail-in ballot](/absentee-mail-ballot/), look for a place 
    where you can indicate you want to vote by mail for multiple elections.`;
  }

  return `Yes, in ${stateName}, you must submit a new mail-in ballot request for each election.`;
};

export const whatIdIsRequiredToVoteByMail = (stateInfo) => {
  const paragraphs = [];

  paragraphs.push('**When requesting a ballot**');
  paragraphs.push(stateInfo.id_laws_absentee_request.text);
  paragraphs.push('**When returning a ballot**');
  paragraphs.push(stateInfo.id_laws_ballot_return.text);

  return paragraphs.join('\n\n');
};

export const canIGetAnEmergencyBallot = (stateInfo, stateName, stateCode) => {
  const paragraphs = [];

  paragraphs.push(
    `Some states have special procedures that allow you to obtain an 
    [emergency ballot](/emergency-absentee-ballots/) if a medical or other emergency 
    occurs after the standard absentee ballot application window. Here are the details for ${stateName}:`
  );
  paragraphs.push(stateInfo.emergency_ballot_laws.text);

  if (
    stateInfo.emergency_ballot_request_deadline.text &&
    stateInfo.emergency_ballot_return_deadline.text
  ) {
    paragraphs.push('The deadlines are as follows:');

    const items = [];
    items.push(
      `* **Deadline to request an emergency ballot**: ${stateInfo.emergency_ballot_request_deadline.text}`
    );
    items.push(
      `* **Deadline to return an emergency ballot**: ${stateInfo.emergency_ballot_return_deadline.text}`
    );

    paragraphs.push(items.join('\n'));
  }

  paragraphs.push(
    `If you have questions or need to submit a request, contact your ${formatLocalElectionOfficeLink(stateCode)}.`
  );

  return paragraphs.join('\n\n');
};

export const howDoesMailInVotingWork = (stateInfo, stateName) => {
  if (stateInfo.absentee_access_levels.text === 'universal') {
    return `All ${stateName} elections are held by mail. 
    This means that ${stateName} will automatically send 
    a mail-in ballot to every registered voter ahead of all elections.`;
  }
  return stateInfo.absentee_process_summary.text;
};

export const canIGetAReplacementBallot = (stateInfo, stateName, stateCode) => {
  if (stateInfo.absentee_access_levels.text === 'universal') {
    if (universalVbmNoTemporary.includes(stateCode)) {
      return stubNoOneTimeRequestsExplanation(stateName, stateCode);
    }

    const paragraphs = [];
    paragraphs.push(
      stubUniversalVbmExplanation(stateInfo, stateName, stateCode)
    );

    paragraphs.push(
      `See below for different ways you can request a one-time ballot if you need one:`
    );

    if (
      stateInfo.absentee_request_methods.multiselect_value.includes('online')
    ) {
      paragraphs.push('**Online**');
      paragraphs.push(stubOnlineInstructions(stateInfo, stateName));
    }

    if (stateInfo.absentee_request_methods.multiselect_value.includes('mail')) {
      paragraphs.push('**By mail**');
      paragraphs.push(stubMailInstructions(stateInfo, stateName, stateCode));
    }

    if (
      stateInfo.absentee_request_methods.multiselect_value.includes('in_person')
    ) {
      paragraphs.push('**In person**');
      paragraphs.push(
        stubInPersonInstructions(stateInfo, stateName, stateCode)
      );
    }

    return paragraphs.join('\n\n');
  }
  return canIHaveABallotMailedToMe(stateInfo, stateName, stateCode);
};

export const whatIDIsRequiredToReturnABallot = (stateInfo) => {
  return stateInfo.id_laws_ballot_return.text;
};

export const whoCanVoteAbsentee = (stateInfo) => {
  return stateInfo.absentee_voting_laws.text;
};

export const howDoIReturnMyBallot = (stateInfo, stateName, stateCode) => {
  if (stateInfo.ballot_return_locations.text === '[]') {
    return `In ${
      stateName
    }, you must return your voted ballot to your ${formatLocalElectionOfficeLink(
      stateCode
    )}
    via mail. It must be ${formatMailDeadline(stateInfo.ballot_return_deadline_mail.text)}.`;
  }

  const paragraphs = [];
  paragraphs.push(`You can always mail your ballot to your ${formatLocalElectionOfficeLink(
    stateCode
  )}.
  If you are mailing your ballot, it must be ${formatMailDeadline(stateInfo.ballot_return_deadline_mail.text)}.`);

  paragraphs.push(`In ${
    stateName
  }, you also have the option to hand-deliver your ballot 
  by ${stripReceivedPostmarkedFromDeadline(
    stateInfo.ballot_return_deadline_in_person.text
  )} to one of these locations:`);

  const locationList = [];
  if (
    stateInfo.ballot_return_locations.multiselect_value.includes(
      'local_election_office'
    )
  ) {
    locationList.push(`- Your ${formatLocalElectionOfficeLink(stateCode)}`);
  }
  if (
    stateInfo.ballot_return_locations.multiselect_value.includes('drop_box')
  ) {
    locationList.push(
      `- A drop box\n    - (As Election Day approaches, you can use our [Where to Vote](/where-to-vote/) tool to find an official drop box.)`
    );
  }
  if (
    stateInfo.ballot_return_locations.multiselect_value.includes(
      'early_voting_site'
    )
  ) {
    locationList.push(
      `- An [early voting site](${getPollingPlaceLocatorLink(stateInfo)})`
    );
  }
  if (
    stateInfo.ballot_return_locations.multiselect_value.includes(
      'polling_place'
    )
  ) {
    locationList.push(
      `- Your [polling place](${getPollingPlaceLocatorLink(stateInfo)})`
    );
  }

  paragraphs.push(locationList.join('\n'));

  if (!stateInfo.ballot_return_locations.text.includes('polling_place')) {
    paragraphs.push(
      'Note: On Election Day, you cannot hand-deliver your ballot to your polling place.'
    );
  }

  return paragraphs.join('\n\n');
};

export const howCanITrackMyMailInBallot = (stateInfo, stateName, stateCode) => {
  const howToCheckPhrase = stateInfo.gov_tool_absentee_tracker.text
    ? `use the [${stateName} ballot tracker tool](${stateInfo.gov_tool_absentee_tracker.text})`
    : `contact your ${formatLocalElectionOfficeLink(stateCode)}`;

  return `If you are voting by mail, ${howToCheckPhrase} to find out 
  if your application has been received, if your ballot has been mailed to you, 
  and if your vote has been counted.`;
};

export const canMyBallotBeRejected = (stateInfo, stateName, stateCode) => {
  const hasInPersonVoting = !electionsByMailOnly.includes(stateCode);

  const paragraphs = [];

  paragraphs.push('Yes, it’s possible for your mail-in ballot to be rejected:');
  paragraphs.push(stateInfo.ballot_curing_laws_rejection_reasons.text);
  paragraphs.push(
    'In some cases, you can resolve issues through [ballot curing](/ballot-curing/). ' +
      'Ballot curing is the process of fixing any problems with your absentee or mail ballot ' +
      'to ensure that your vote is counted. You will have very little time to fix issues with your ballot, so act quickly.'
  );

  const steps = [];

  if (stateInfo.gov_tool_absentee_tracker.text) {
    steps.push(
      `1. Use the [${stateName} online ballot tracking tool](${stateInfo.gov_tool_absentee_tracker.text}) 
      to check whether your ballot has been received and counted.`
    );
  } else {
    steps.push(
      `1. Contact your ${formatLocalElectionOfficeLink(stateCode)} to check whether your ballot has been received and counted.`
    );
  }
  if (hasInPersonVoting) {
    steps.push(
      `2. If your absentee ballot was rejected and it's before Election Day, you can probably still vote 
      in person on Election Day at your [polling place](/where-to-vote/). 
      Your ${formatLocalElectionOfficeLink(stateCode)} can provide more information about 
      the rules around voting in person after your ballot was rejected.`
    );
    steps.push(
      `3. If your ballot was rejected and you are unable to vote in person, contact your ${formatLocalElectionOfficeLink(stateCode)} 
       as soon as possible to see if you can correct the issue.`
    );
  } else {
    steps.push(
      `2. If your ballot was rejected, contact your ${formatLocalElectionOfficeLink(stateCode)} 
       as soon as possible to see if you can correct the issue.`
    );
  }

  paragraphs.push(steps.join('\n'));

  return paragraphs.join('\n\n');
};

export const howDoIVoteIfMilitaryOrOverseas = (stateInfo) => {
  return `The [Federal Voting Assistance Program](${stateInfo.leo_overseas_voters.text}) can help you to vote.`;
};
